import { create } from 'zustand';
import type { Message, Conversation, Artifact, StreamMessage, InteractiveMode, ProfileProgress } from '@/types';
import { documentCardOf } from '@/lib/plans';
import { useDocumentsStore } from './documentsStore';
import { chat, ensureFreshToken } from '@/lib/api';
import { useSetupStore } from '@/stores/setupStore';
import { useBrowserStore } from '@/stores/browserStore';
import { streamEnding } from '@/stores/streamEnding';

// True while a CHAT-triggered recipe run holds the "ZAC is driving" overlay.
// Tracked separately from browserStore.agentControl so stream cleanup never
// stomps an overlay owned by the guided-setup / write-enable flows.
//
// Once a turn starts driving, the overlay stays up until the turn ends
// (done / error / stream closed), not just until the tool call returns: the
// brain may keep working (compose the reply, retry another tool) and the page
// can still be settling, so dropping it early read as "it stopped before it
// finished".
let chatDriving = false;

// Incremented by every send. A stream only touches the store's streaming
// state while its turn is still the newest one (streamEnding.ts).
let turnSeq = 0;

function setChatDriving(on: boolean, status: string | null = null) {
  if (on) {
    chatDriving = true;
    useBrowserStore.getState().setAgentControl(true);
    useBrowserStore.getState().setDrivingStatus(status);
  } else if (chatDriving) {
    chatDriving = false;
    useBrowserStore.getState().setAgentControl(false);
    useBrowserStore.getState().setDrivingStatus(null);
  }
}

// Monotonic suffix so rapidly-injected client messages never collide on a
// same-millisecond Date.now() id (which would dupe React keys).
let clientMsgSeq = 0;
const clientMsgId = () => `msg_${Date.now()}_${clientMsgSeq++}`;

// Persist a client-authored USER turn (guided-setup injected messages) so it
// survives a hard reload — the store append alone is in-memory only.
//
// Single-writer invariant: the client may ONLY persist its own user turns.
// Assistant rows are written server-side (streaming replies by the brain, and
// each guided-setup step's canonical line by browser/intro-api), so this fires
// with role 'user' exclusively. Fire-and-forget: a persist failure must never
// break the live UX, but it is logged rather than silently swallowed.
function persistUserMessage(conversationId: string, content: string): void {
  void chat.appendMsg(conversationId, { role: 'user', content }).catch((err) => {
    console.warn('[chat] failed to persist user turn', err);
  });
}

interface ChatState {
  conversations: Conversation[];
  currentConversationId: string | null;
  messages: Message[];
  isLoading: boolean;
  isStreaming: boolean;
  streamingContent: string;
  streamingArtifacts: Artifact[];
  // Interactive options from stream
  pendingInteractive: InteractiveMode | null;
  // Profile progress from stream
  profilingProgress: ProfileProgress | null;
  // Evaluation data from stream
  pendingEvaluationId: string | null;
  pendingConfidenceScore: number | null;
  pendingConfidenceRationale: string | null;
  pendingCitations: Message['citations'] | null;
  /** Server-issued id for the in-flight assistant turn (from message_start),
   *  used to anchor the finalized message to the persisted row. */
  pendingMessageId: string | null;
  /** Why the in-flight turn ended (from done) — 'cancelled' marks a partial. */
  pendingStopReason: import('@/types').StopReason | null;
  thinkingStatus: string | null;
  error: string | null;
  /** Last content sent via sendMessage. Used by retryLastMessage on failure. */
  lastUserMessage: string | null;

  // Actions
  loadConversations: () => Promise<void>;
  /** A new conversation, titled when the caller knows its subject (a file attached). */
  createConversation: (title?: string) => Promise<string>;
  selectConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  /** Guided-setup app-name step: create the user's S2S app from the supplied
   *  name instead of sending the name to the chat brain. Shared by the inline
   *  interactive widget AND the main composer (via sendMessage's guard). */
  /** Guided-setup: fill the Information tab (activation prerequisites), drive
   *  the Add-Scopes modal, then chain straight into activate/verify. Manual
   *  handoff only as fallback when the modal can't be driven. Idempotent —
   *  also the resume re-entry point when an app exists but isn't connected. */
  /** Guided-setup finale (after scopes are added): activate the app, capture
   *  the Zoom identity, mint a real S2S token, and report the granted-scope
   *  diff. Idempotent — safe to re-run on retry. */
  /** Re-run the Step-1 credential capture (the app exists; the read failed or
   *  raced). On success the chain continues into info + scopes. */
  retryLastMessage: () => Promise<void>;
  addAssistantMessage: (content: string, artifacts?: Artifact[], interactive?: InteractiveMode, opts?: { once?: boolean; persist?: boolean }) => Promise<void>;
  addUserMessage: (content: string, opts?: { once?: boolean }) => Promise<void>;
  handleStreamMessage: (message: StreamMessage) => void;
  finalizeStream: () => void;
  clearError: () => void;
  clearCurrentConversation: () => void;
  setActiveConversation: (id: string) => void;
  clearMessageInteractive: (messageId: string) => void;
  /** Swap a message's card for another (a request's card becomes its plan's). */
  setMessageInteractive: (messageId: string, interactive: InteractiveMode) => void;
  /** A requirements file attached in the chat: read as a document of the conversation, its card under it. */
  attachDocument: (file: File) => Promise<void>;

}

export const useChatStore = create<ChatState>((set, get) => ({
  conversations: [],
  currentConversationId: null,
  messages: [],
  isLoading: false,
  isStreaming: false,
  streamingContent: '',
  streamingArtifacts: [],
  pendingInteractive: null,
  profilingProgress: null,
  pendingEvaluationId: null,
  pendingConfidenceScore: null,
  pendingConfidenceRationale: null,
  pendingCitations: null,
  pendingMessageId: null,
  pendingStopReason: null,
  thinkingStatus: null,
  error: null,
  lastUserMessage: null,

  loadConversations: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await chat.listConvs({});
      set({ conversations: response.items as Conversation[] });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load conversations' });
    } finally {
      set({ isLoading: false });
    }
  },

  createConversation: async (title?: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await chat.createConv(title ? { title } : {});
      const conversation = response.conversation as Conversation;
      set((state) => ({
        conversations: [conversation, ...state.conversations],
        currentConversationId: conversation.id,
        messages: [],
      }));
      return conversation.id;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to create conversation' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  selectConversation: async (id: string) => {
    set({ isLoading: true, error: null, currentConversationId: id });
    try {
      const response = await chat.getConv(id);
      set({ messages: response.messages as Message[] });

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load conversation' });
    } finally {
      set({ isLoading: false });
    }
  },

  deleteConversation: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await chat.deleteConv(id);
      set((state) => ({
        conversations: state.conversations.filter(c => c.id !== id),
        currentConversationId: state.currentConversationId === id ? null : state.currentConversationId,
        messages: state.currentConversationId === id ? [] : state.messages,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete conversation' });
    } finally {
      set({ isLoading: false });
    }
  },

  sendMessage: async (content: string) => {
    // Guided-setup interception (defect #1): while ZAC is awaiting the Zoom
    // app name, the next message — typed in the inline widget OR the main
    // composer — IS the name. Route it to S2S creation, NEVER to the chat
    // brain (which would misclassify it as a question and tool-flail). This is
    // the single send choke point, so the main box can no longer bypass the
    // awaitingAppName guard that previously lived only on the inline widget.
    if (useSetupStore.getState().awaitingAppName && !content.startsWith('zoom-setup:')) {
      useSetupStore.getState().setAwaitingAppName(false);
      if (get().pendingInteractive) set({ pendingInteractive: null });
      const { submitAppName } = await import('./guidedSetup');
      await submitAppName(content.trim());
      return;
    }

    const { currentConversationId, createConversation } = get();

    let conversationId = currentConversationId;
    if (!conversationId) {
      conversationId = await createConversation();
    }

    // Add user message to the list immediately
    const userMessage: Message = {
      id: `temp_${Date.now()}`,
      conversationId,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, userMessage],
      isStreaming: true,
      streamingContent: '',
      streamingArtifacts: [],
      error: null,
      lastUserMessage: content,
    }));

    const turn = ++turnSeq;
    const isCurrentTurn = () => turn === turnSeq;
    let sawDone = false;
    let sawError = false;
    try {
      // The stream is a WebSocket: refresh a token about to expire first, since its handshake
      // can't retry a 401.
      await ensureFreshToken();
      // Use the generated Encore client for streaming
      const stream = await chat.streamChat({
        conversationId,
        message: content,
        // Lets ZAC ask "watch or just do it?" before changing something in a
        // browser the user can't currently see.
        browserVisible: useBrowserStore.getState().panelOpen,
      });

      // Read this turn's frames until its `done`, then let the stream go:
      // waiting on for the server's close only kept a dead socket open (and,
      // when it finally closed, made it look like the next turn's failure).
      try {
        for await (const message of stream) {
          get().handleStreamMessage(message);
          if (message.type === 'error') sawError = true;
          if (message.type === 'done') { sawDone = true; break; }
        }
      } finally {
        stream.close();
      }

      // Stream ended without its own `done`/`error` — the relay dropped while
      // the brain was still tool-looping, or a slow turn got cut. Guarantee
      // closure for THIS turn only: commit whatever content arrived, otherwise
      // clear the spinner and surface a retry bubble. An older stream that
      // ends while a newer turn runs changes nothing (streamEnding.ts).
      const ending = streamEnding({
        sawDone,
        sawError,
        isCurrentTurn: isCurrentTurn(),
        hasContent: get().streamingContent.trim().length > 0,
      });
      if (ending === 'finalize') {
        get().finalizeStream();
      } else if (ending === 'dropped') {
        set({
          isStreaming: false,
          thinkingStatus: null,
          streamingContent: '',
          streamingArtifacts: [],
          error: 'The connection dropped before a reply came through. Tap retry to try again.',
        });
      }

    } catch (error) {
      if (isCurrentTurn()) {
        set({
          isStreaming: false,
          thinkingStatus: null,
          error: error instanceof Error ? error.message : 'Failed to send message',
        });
      }
    } finally {
      // The turn is over however the stream ended — unless a newer turn owns
      // the overlay now.
      if (isCurrentTurn()) setChatDriving(false);
    }
  },





  retryLastMessage: async () => {
    const last = get().lastUserMessage;
    if (!last) return;
    // Drop the trailing failed-user-message bubble if it was the last thing
    // we appended, so retry doesn't stack duplicate user bubbles.
    set((state) => {
      const msgs = state.messages;
      const tail = msgs[msgs.length - 1];
      if (tail && tail.role === 'user' && tail.content === last) {
        return { messages: msgs.slice(0, -1), error: null };
      }
      return { error: null };
    });
    await get().sendMessage(last);
  },

  addAssistantMessage: async (content: string, artifacts?: Artifact[], interactive?: InteractiveMode, opts?: { once?: boolean; persist?: boolean }) => {
    // Idempotency guard for re-derivable guided-setup narration: skip if an
    // identical assistant message already exists in the conversation. On reload
    // the persisted copy loads first, so the setup hook / ChatPage re-render
    // no-ops here instead of stacking duplicates.
    if (opts?.once && get().messages.some((m) => m.role === 'assistant' && m.content === content)) return;
    const { currentConversationId, createConversation } = get();

    let conversationId = currentConversationId;
    if (!conversationId) {
      conversationId = await createConversation();
    }

    const assistantMessage: Message = {
      id: clientMsgId(),
      conversationId,
      role: 'assistant',
      content,
      artifacts,
      interactive,
      createdAt: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, assistantMessage],
    }));
    // Assistant turns are NOT persisted from the client — the transcript's
    // assistant rows are written server-side (guided-setup steps persist their
    // own canonical line via browser/intro-api). This message is live-only;
    // on reload the server-authored line takes its place. The `opts.persist`
    // flag is retained for call-site compatibility but no longer writes.
  },

  addUserMessage: async (content: string, opts?: { once?: boolean }) => {
    if (opts?.once && get().messages.some((m) => m.role === 'user' && m.content === content)) return;
    const { currentConversationId, createConversation } = get();

    let conversationId = currentConversationId;
    if (!conversationId) {
      conversationId = await createConversation();
    }

    const userMessage: Message = {
      id: clientMsgId(),
      conversationId,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, userMessage],
    }));
    persistUserMessage(conversationId, content);
  },

  handleStreamMessage: (message: StreamMessage) => {
    switch (message.type) {
      case 'message_start':
        // Server announced the id it will persist this assistant turn under —
        // hold it so finalizeStream anchors the rendered message to the same
        // id the DB row will have (survives reload; enables feedback later).
        if (message.messageId) set({ pendingMessageId: message.messageId });
        break;
      case 'token':
        if (message.content) {
          set((state) => ({
            streamingContent: state.streamingContent + message.content,
          }));
        }
        break;
      case 'artifact':
        if (message.artifact) {
          set((state) => ({
            streamingArtifacts: [...state.streamingArtifacts, message.artifact!],
          }));
        }
        break;
      case 'thinking':
        set({ thinkingStatus: message.content ?? null });
        break;
      case 'interactive':
        if (message.interactive) {
          set({ pendingInteractive: message.interactive });
        }
        break;
      case 'progress': {
        const p = message.progress;
        if (p?.phase === 'browser-panel') {
          // The user asked to show/hide the browser (setBrowserPanel tool) —
          // flip the same state the "Show Browser Interface" toggle drives.
          if (p.visible) useBrowserStore.getState().openPanel();
          else useBrowserStore.getState().closePanel();
        } else if (p?.phase === 'browser-driving') {
          setChatDriving(true, p.status ?? null);
        } else if (p?.phase === 'browser-driving-done') {
          if (chatDriving) useBrowserStore.getState().setDrivingStatus('Still working on it…');
        } else if (p?.phase === 'profiling') {
          set({ profilingProgress: p });
        }
        break;
      }
      case 'error':
        setChatDriving(false); // never leave the overlay stuck on a failed turn
        set({
          isStreaming: false,
          error: message.error ?? 'An error occurred',
        });
        break;
      case 'done':
        setChatDriving(false); // the turn is complete — release the overlay
        // Capture evaluation data before finalizing
        set({
          pendingEvaluationId: message.evaluationId ?? null,
          pendingConfidenceScore: message.confidenceScore ?? null,
          pendingConfidenceRationale: message.confidenceRationale ?? null,
          pendingCitations: message.citations ?? null,
          // done echoes messageId — fall back to it if message_start was missed.
          pendingMessageId: message.messageId ?? get().pendingMessageId,
          pendingStopReason: message.stopReason ?? null,
        });
        get().finalizeStream();
        break;
    }
  },

  finalizeStream: () => {
    const {
      currentConversationId,
      streamingContent,
      streamingArtifacts,
      pendingInteractive,
      pendingEvaluationId,
      pendingConfidenceScore,
      pendingConfidenceRationale,
      pendingCitations,
      pendingMessageId,
      pendingStopReason,
    } = get();

    if (!currentConversationId) return;

    // Guard against committing a blank assistant turn. A stream that produced
    // no text, no artifacts, and no interactive options must not become a
    // rendered bubble (avatar + empty pill) — clear the streaming state and
    // let the error path (if any) surface instead. An interactive-only or
    // artifact-only turn is legitimate and still commits below.
    const hasContent = streamingContent.trim().length > 0;
    const hasArtifacts = streamingArtifacts.length > 0;
    const hasInteractive = !!pendingInteractive;
    if (!hasContent && !hasArtifacts && !hasInteractive) {
      set({
        isStreaming: false,
        streamingContent: '',
        streamingArtifacts: [],
        pendingInteractive: null,
        profilingProgress: null,
        thinkingStatus: null,
        pendingEvaluationId: null,
        pendingConfidenceScore: null,
        pendingConfidenceRationale: null,
        pendingCitations: null,
        pendingMessageId: null,
        pendingStopReason: null,
      });
      return;
    }

    const assistantMessage: Message = {
      // Prefer the server-issued id (matches the persisted row on reload);
      // fall back to a client id only if the stream never announced one.
      id: pendingMessageId ?? `msg_${Date.now()}`,
      conversationId: currentConversationId,
      role: 'assistant',
      content: streamingContent,
      artifacts: streamingArtifacts.length > 0 ? streamingArtifacts : undefined,
      interactive: pendingInteractive ?? undefined,
      citations: pendingCitations ?? undefined,
      stopReason: pendingStopReason ?? undefined,
      evaluationId: pendingEvaluationId ?? undefined,
      confidenceScore: pendingConfidenceScore ?? undefined,
      confidenceRationale: pendingConfidenceRationale ?? undefined,
      createdAt: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, assistantMessage],
      isStreaming: false,
      streamingContent: '',
      streamingArtifacts: [],
      pendingInteractive: null,
      profilingProgress: null,
      thinkingStatus: null,
      pendingEvaluationId: null,
      pendingConfidenceScore: null,
      pendingConfidenceRationale: null,
      pendingCitations: null,
      pendingMessageId: null,
      pendingStopReason: null,
    }));

    // Update conversation in the list
    set((state) => ({
      conversations: state.conversations.map(c =>
        c.id === currentConversationId
          ? { ...c, lastMessage: streamingContent.slice(0, 100), updatedAt: new Date().toISOString() }
          : c
      ),
    }));
  },

  clearError: () => set({ error: null }),

  clearCurrentConversation: () => set({
    currentConversationId: null,
    messages: [],
    error: null,
  }),

  // Drop the interactive options from a message (e.g. after the user picks a
  // choice) so the buttons disappear instead of lingering.
  clearMessageInteractive: (messageId: string) => set((state) => ({
    messages: state.messages.map((m) =>
      m.id === messageId ? { ...m, interactive: undefined } : m
    ),
  })),

  setMessageInteractive: (messageId: string, interactive: InteractiveMode) => set((state) => ({
    messages: state.messages.map((m) =>
      m.id === messageId ? { ...m, interactive } : m
    ),
  })),

  attachDocument: async (file: File) => {
    try {
      const conversationId = get().currentConversationId ?? (await get().createConversation(file.name));
      const id = await useDocumentsStore.getState().upload(file, conversationId);
      // The user moved to another conversation meanwhile: the request stays with the one it was attached in.
      if (get().currentConversationId !== conversationId) return;
      if (id === null) {
        await get().addAssistantMessage(`I couldn't read ${file.name}: ${useDocumentsStore.getState().uploadError ?? 'the upload failed'}.`, undefined, undefined, { persist: false });
        return;
      }
      await get().addUserMessage(`Attached **${file.name}** ([follow it](/changes/documents/${id}))`);
      await get().addAssistantMessage(`I'm reading **${file.name}**. Its questions and then the plan show below.`, undefined, documentCardOf(id, file.name), { persist: false });
    } catch (err) {
      await get().addAssistantMessage(`I couldn't attach ${file.name}: ${err instanceof Error ? err.message : String(err)}.`, undefined, undefined, { persist: false });
    }
  },

  // Make a conversation active WITHOUT loading it from the backend. Used for
  // the client-only guided-setup chat, whose messages are rebuilt locally — a
  // backend load would race the rehydrate and wipe the injected prompt.
  setActiveConversation: (id: string) => set({
    currentConversationId: id,
    messages: [],
    error: null,
  }),




}));
